'use client';

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

/**
 * Newsletter sign-up.
 *
 * <p>With JavaScript it posts `{ email }` as JSON to the storefront's generic
 * forms endpoint, `POST /api/forms/{formKey}/submit`, including the two
 * anti-spam fields that route expects (`_hp` honeypot and `_elapsedMs`). The
 * form with that key must exist in the admin form designer with an `email`
 * field [confirm].
 *
 * <p>Without JavaScript the same form submits as a plain GET to
 * `fallbackHref` (the contact page by default), because the JSON endpoint
 * cannot accept a browser's form-encoded POST.
 */
export interface NewsletterFormProps {
  title: string;
  body?: string;
  label: string;
  placeholder?: string;
  buttonLabel: string;
  successMessage: string;
  errorMessage: string;
  formKey: string;
  fallbackHref: string;
  note?: string;
}

export function NewsletterForm(props: NewsletterFormProps) {
  const inputId = useId();
  const statusId = useId();
  const mountedAt = useRef<number>(0);
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');

  useEffect(() => {
    mountedAt.current = Date.now();
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setState('sending');
    try {
      const response = await fetch(`/api/forms/${encodeURIComponent(props.formKey)}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: String(data.get('email') ?? ''),
          marketingOptIn: data.get('marketingOptIn') === 'on',
          _hp: String(data.get('_hp') ?? ''),
          _elapsedMs: Date.now() - mountedAt.current,
        }),
      });
      const result = await response.json().catch(() => null);
      setState(response.ok && result?.discarded !== true ? 'done' : 'error');
    } catch {
      setState('error');
    }
  }

  return (
    <div className="sk-newsletter">
      <h2 className="sk-heading sk-footer__heading">{props.title}</h2>
      {props.body && <p className="sk-footer__text">{props.body}</p>}
      <form className="sk-newsletter__form" method="get" action={props.fallbackHref} onSubmit={onSubmit}>
        <label htmlFor={inputId} className="sk-footer__label">
          {props.label}
        </label>
        <div className="sk-newsletter__row">
          <input
            id={inputId}
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder={props.placeholder || undefined}
            className="sk-newsletter__input"
            aria-describedby={statusId}
          />
          <button type="submit" className="sk-btn sk-btn--primary" disabled={state === 'sending' || state === 'done'}>
            {props.buttonLabel}
          </button>
        </div>
        <label className="sk-footer__note" style={{ display: 'flex', gap: '.5rem', alignItems: 'start', marginTop: '.75rem' }}>
          <input name="marketingOptIn" type="checkbox" required />
          <span>Email me kitchen news and offers; I can unsubscribe at any time.</span>
        </label>
        {/* Honeypot: hidden from people and assistive technology, filled only by bots. */}
        <input
          type="text"
          name="_hp"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="sk-visually-hidden"
          defaultValue=""
        />
        <p id={statusId} role="status" className="sk-footer__status">
          {state === 'done' ? props.successMessage : state === 'error' ? props.errorMessage : ''}
        </p>
        {props.note && <p className="sk-footer__note">{props.note}</p>}
      </form>
    </div>
  );
}
